import React from 'react';
import {useIntl} from 'react-intl';
import {useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {Card, Col, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap';
import ReactSelect, {SingleValue} from 'react-select';
import {PageTitle} from '../../../_metronic/layout/core';
import Level3Title from '../../../app/common/components/title/Level3Title';
import {useGetPacketPowerBaies, useGetPacketPowerGraph} from '../Hooks/PacketPowerManager';
import styles from '../../../app/pages/configuration-list/configuration/cis/config-monitoring.module.scss';
import tableStyles from '../../../app/common/components/table/TableMetronic.module.scss';
import useIsMobile from '../../../app/common/components/table/useIsMobile';

const INSTANT_T_HOURS = 24;
const J90_HOURS = 90 * 24;

interface GraphPopoverContentProps {
  serviceId: number;
  metricId: number;
  metricName: string;
  cable: string;
  periodHours: number;
  title: string;
  enabled: boolean;
  onClose: () => void;
}

const GraphPopoverContent = ({
  serviceId,
  metricId,
  metricName,
  cable,
  periodHours,
  title,
  enabled,
  onClose,
}: GraphPopoverContentProps): JSX.Element => {
  const intl = useIntl();
  const {data, isLoading, isError} = useGetPacketPowerGraph(
    serviceId,
    metricId,
    metricName,
    cable,
    periodHours,
    enabled
  );

  // Le sélecteur est construit à partir des graphes renvoyés par l'endpoint, en plaçant
  // « Total » en premier ; les autres câbles conservent leur ordre d'origine.
  const graphs = React.useMemo(() => {
    const list = data?.graphs ?? [];
    const total = list.filter((graph) => graph.label === 'Total');
    const others = list.filter((graph) => graph.label !== 'Total');
    return [...total, ...others];
  }, [data]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // À l'arrivée des données, on présélectionne le câble de la page (sinon le premier graphe).
  React.useEffect(() => {
    if (graphs.length === 0) return;
    const defaultIndex = graphs.findIndex((graph) => graph.label === cable);
    setSelectedIndex(defaultIndex >= 0 ? defaultIndex : 0);
  }, [graphs, cable]);

  const selected = graphs[selectedIndex];

  const cableOptions = React.useMemo(
    () => graphs.map((graph, index) => ({value: index, label: graph.label})),
    [graphs]
  );

  return (
    <div className={styles.tooltipContent}>
      <div className={styles.tooltipTitle}>
        <span>{title}</span>
        <button
          type="button"
          className={styles.tooltipCloseButton}
          onClick={onClose}
          aria-label={intl.formatMessage({id: 'MONITORING.GRAPH.CLOSE'})}
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
      {graphs.length > 0 && (
        <div className="px-3 pt-3 d-flex align-items-center gap-2">
          <span className="fw-bold text-nowrap">
            {intl.formatMessage({id: 'CABLE.DETAILS.SELECT.LABEL'})}
          </span>
          <div className="flex-grow-1">
            <ReactSelect<{value: number; label: string}, false>
              isSearchable={false}
              value={cableOptions[selectedIndex] ?? null}
              onChange={(option: SingleValue<{value: number; label: string}>) =>
                option && setSelectedIndex(option.value)
              }
              options={cableOptions}
              classNamePrefix="graph-period-select"
            />
          </div>
        </div>
      )}
      {isLoading && (
        <div className="text-center p-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && (
        <div className="text-center p-4 text-danger">Erreur lors du chargement du graphe.</div>
      )}
      {!isLoading && !isError && selected && (
        <img
          src={`data:image/png;base64,${selected.img}`}
          alt="CELESTE Monitoring"
          className={styles.graphImage}
        />
      )}
      {!isLoading && !isError && !selected && (
        <div className="text-center p-4 text-muted">Graphe indisponible.</div>
      )}
    </div>
  );
};

const CableDetailsPage = (): JSX.Element => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const serviceId = Number(searchParams.get('serviceId') ?? 0);
  const cable = searchParams.get('cable') ?? '';

  const [openTooltipId, setOpenTooltipId] = React.useState<string | null>(null);
  const {isLoading, isError, data: metrics} = useGetPacketPowerBaies(serviceId, cable);
  const isMobile = useIsMobile();

  const renderGraphTooltip = (
    tooltipKey: string,
    title: string,
    metricId: number,
    metricName: string,
    periodHours: number
  ) => (
    <Tooltip id={`graph-tooltip-${tooltipKey}`} className={styles.graphTooltip}>
      <GraphPopoverContent
        serviceId={serviceId}
        metricId={metricId}
        metricName={metricName}
        cable={cable}
        periodHours={periodHours}
        title={title}
        enabled={openTooltipId === tooltipKey}
        onClose={() => setOpenTooltipId(null)}
      />
    </Tooltip>
  );

  const renderGraphButton = (
    tooltipKey: string,
    title: string,
    metricId: number,
    metricName: string,
    periodHours: number,
    buttonLabel?: string
  ): JSX.Element => (
    <OverlayTrigger
      placement={isMobile ? 'auto' : 'left'}
      overlay={renderGraphTooltip(tooltipKey, title, metricId, metricName, periodHours)}
      show={openTooltipId === tooltipKey}
      trigger="click"
      onToggle={(nextShow) => {
        if (nextShow) setOpenTooltipId(tooltipKey);
      }}
    >
      <button className={clsx('btn btn-sm btn-light-primary', styles.mobileActionButton)}>
        <i className="fa fa-signal"></i>
        {buttonLabel && <span className="ms-2">{buttonLabel}</span>}
      </button>
    </OverlayTrigger>
  );

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BAIE.DETAILS.TITLE'})}</PageTitle>

      <div className={clsx(styles.containerConfigMonitoring)}>
        <Row className="mt-4 mb-6">
          <Col md={12}>
            <Card>
              <Card.Header className="border-0 pt-6">
                <Card.Title>
                  <Level3Title
                    title={intl.formatMessage({id: 'BAIE.DETAILS.SUBTITLE'}, {serviceId})}
                  />
                </Card.Title>
              </Card.Header>
              <Card.Body className="table-responsive py-4">
                {isLoading && (
                  <div className="text-center p-4">
                    <Spinner animation="border" />
                  </div>
                )}
                {isError && (
                  <div className="text-center p-4 text-danger">
                    Erreur lors du chargement des métriques.
                  </div>
                )}
                {!isLoading && !isError && (metrics ?? []).length === 0 && (
                  <div className="text-center p-4">Aucune métrique disponible pour ce câble.</div>
                )}
                {!isLoading && !isError && (metrics ?? []).length > 0 && isMobile && (
                  <div>
                    {(metrics ?? []).map((metric) => (
                      <div key={metric.metricId} className={styles.mobileLinkCard}>
                        <div className={styles.mobileCardHeader}>
                          <span className="fw-bold">{metric.serviceDescription}</span>
                        </div>
                        <div className={styles.mobileCardRow}>
                          <span className={styles.mobileCardLabel}>
                            {intl.formatMessage({id: 'CABLE.DETAILS.COL.METRIQUE'})}
                          </span>
                          <span>{metric.label}</span>
                        </div>
                        <div className={styles.mobileCardActions}>
                          {renderGraphButton(
                            `${metric.metricId}-instant`,
                            `${metric.label} — ${intl.formatMessage({
                              id: 'CABLE.DETAILS.COL.INSTANT_T',
                            })}`,
                            metric.metricId,
                            metric.metricName,
                            INSTANT_T_HOURS,
                            intl.formatMessage({id: 'CABLE.DETAILS.COL.INSTANT_T'})
                          )}
                          {renderGraphButton(
                            `${metric.metricId}-j90`,
                            `${metric.label} — ${intl.formatMessage({
                              id: 'CABLE.DETAILS.COL.J90',
                            })}`,
                            metric.metricId,
                            metric.metricName,
                            J90_HOURS,
                            intl.formatMessage({id: 'CABLE.DETAILS.COL.J90'})
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!isLoading && !isError && (metrics ?? []).length > 0 && !isMobile && (
                  <table
                    className={clsx(
                      'table table-striped align-middle table-row-dashed fs-5 dataTable',
                      tableStyles.ynex_table
                    )}
                  >
                    <thead>
                      <tr className="text-muted fw-bolder fs-5 gs-0">
                        <th>{intl.formatMessage({id: 'CABLE.DETAILS.COL.GRAPHIQUES'})}</th>
                        <th>{intl.formatMessage({id: 'CABLE.DETAILS.COL.METRIQUE'})}</th>
                        <th>{intl.formatMessage({id: 'CABLE.DETAILS.COL.INSTANT_T'})}</th>
                        <th>{intl.formatMessage({id: 'CABLE.DETAILS.COL.J90'})}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold">
                      {(metrics ?? []).map((metric) => (
                        <tr key={metric.metricId}>
                          <td>{metric.serviceDescription}</td>
                          <td>{metric.label}</td>
                          <td>
                            {renderGraphButton(
                              `${metric.metricId}-instant`,
                              `${metric.label} — ${intl.formatMessage({
                                id: 'CABLE.DETAILS.COL.INSTANT_T',
                              })}`,
                              metric.metricId,
                              metric.metricName,
                              INSTANT_T_HOURS
                            )}
                          </td>
                          <td>
                            {renderGraphButton(
                              `${metric.metricId}-j90`,
                              `${metric.label} — ${intl.formatMessage({
                                id: 'CABLE.DETAILS.COL.J90',
                              })}`,
                              metric.metricId,
                              metric.metricName,
                              J90_HOURS
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CableDetailsPage;
